/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2026 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 20-08-2026 16:57:03
 * Autor     : Diego Brock
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';

/** Ação retornada ao fechar o router de gateway. */
export type CommandGatewayRouterAction = 'open-modern' | 'open-legacy';

/** Payload de saída do dialog intermediário. */
export interface CommandGatewayRouterResult {
  action: CommandGatewayRouterAction;
  selected: any[];
}

/** Dados de entrada do dialog intermediário. */
export interface CommandGatewayRouterData {
  modern: any[];
  legacy: any[];
  canOpenModern: boolean;
  canOpenLegacy: boolean;
}

/**
 * Dialog intermediário da ferramenta Selecionar quando a seleção mistura
 * terminais da nova arquitetura (`T`) e legados.
 */
@Component({
  selector: 'app-command-gateway-router-dialog',
  templateUrl: './command-gateway-router-dialog.component.html',
  styleUrls: ['./command-gateway-router-dialog.component.scss'],
})
export class CommandGatewayRouterDialogComponent implements OnInit {
  displayedColumns: string[] = ['select', 'plate'];

  modernDataSource = new MatTableDataSource<any>([]);
  legacyDataSource = new MatTableDataSource<any>([]);
  modernSelection = new SelectionModel<any>(true, []);
  legacySelection = new SelectionModel<any>(true, []);

  canOpenModern = false;
  canOpenLegacy = false;

  constructor(
    public dialogRef: MatDialogRef<CommandGatewayRouterDialogComponent, CommandGatewayRouterResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: CommandGatewayRouterData
  ) {}

  ngOnInit(): void {
    const modern = Array.isArray(this.data?.modern) ? this.data.modern : [];
    const legacy = Array.isArray(this.data?.legacy) ? this.data.legacy : [];
    this.canOpenModern = !!this.data?.canOpenModern && modern.length > 0;
    this.canOpenLegacy = !!this.data?.canOpenLegacy && legacy.length > 0;

    this.modernDataSource = new MatTableDataSource<any>(modern);
    this.legacyDataSource = new MatTableDataSource<any>(legacy);
    this.modernDataSource.filterPredicate = this.plateFilterPredicate;
    this.legacyDataSource.filterPredicate = this.plateFilterPredicate;
    this.modernSelection = new SelectionModel<any>(true, []);
    this.legacySelection = new SelectionModel<any>(true, []);
  }

  /**
   * Filtro por placa (case-insensitive).
   */
  private plateFilterPredicate = (row: any, filter: string): boolean => {
    return (row.placa || '').toLowerCase().indexOf(filter) !== -1;
  };

  /**
   * Fecha o router sem abrir popup de comandos.
   */
  close(): void {
    this.dialogRef.close(null);
  }

  /**
   * Abre o fluxo moderno com os terminais marcados do grupo T.
   */
  openModern(): void {
    if (!this.canOpenModern || !this.modernSelection.selected.length) {
      return;
    }
    this.dialogRef.close({
      action: 'open-modern',
      selected: [...this.modernSelection.selected],
    });
  }

  /**
   * Abre o fluxo legado com os terminais marcados do grupo legado.
   */
  openLegacy(): void {
    if (!this.canOpenLegacy || !this.legacySelection.selected.length) {
      return;
    }
    this.dialogRef.close({
      action: 'open-legacy',
      selected: [...this.legacySelection.selected],
    });
  }

  isAllSelected(source: MatTableDataSource<any>, selection: SelectionModel<any>): boolean {
    const rows = source.filteredData || source.data || [];
    return rows.length > 0 && rows.every((row) => selection.isSelected(row));
  }

  masterToggle(source: MatTableDataSource<any>, selection: SelectionModel<any>): void {
    const rows = source.filteredData || source.data || [];
    if (this.isAllSelected(source, selection)) {
      rows.forEach((row) => selection.deselect(row));
    } else {
      rows.forEach((row) => selection.select(row));
    }
  }

  toggleRow(row: any, selection: SelectionModel<any>): void {
    selection.toggle(row);
  }

  handleFilter(source: MatTableDataSource<any>, value: string): void {
    source.filter = (value || '').trim().toLowerCase();
  }
}
